import {
  collection,
  addDoc,
  doc,
  setDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  getDocs,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  notifyAdminNewMessage,
  notifyStudentNewMessage,
} from './notifications';

function threadIdFor(studentUserId) {
  return `student-${studentUserId}`;
}

/** Send a message in a student-admin thread */
export async function sendMessage({ fromUid, fromRole, fromName, studentUserId, text }) {
  const trimmed = (text || '').trim();
  if (!trimmed || !studentUserId) throw new Error('Message and student are required.');

  const threadId = threadIdFor(studentUserId);
  const clientAt = Date.now();
  const msg = {
    threadId,
    studentUserId,
    fromUid: fromUid || '',
    fromRole: fromRole || 'student',
    fromName: fromName || '',
    text: trimmed,
    read: false,
    clientAt,
    createdAt: serverTimestamp(),
  };

  await addDoc(collection(db, 'messages'), msg);

  const threadPayload = {
    studentUserId,
    lastMessage: trimmed.slice(0, 120),
    lastMessageAt: serverTimestamp(),
    lastFromRole: fromRole,
    updatedAt: serverTimestamp(),
  };
  if (fromRole === 'student' && fromName) {
    threadPayload.studentName = fromName;
  }

  await setDoc(doc(db, 'messageThreads', threadId), threadPayload, { merge: true });

  if (fromRole === 'student') {
    await notifyAdminNewMessage({
      studentName: fromName,
      studentUserId,
      preview: trimmed,
    }).catch(console.warn);
  } else if (fromRole === 'admin') {
    await notifyStudentNewMessage({ userId: studentUserId, preview: trimmed }).catch(console.warn);
  }

  return msg;
}

function sortMessages(docs) {
  return docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const aMs = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || a.clientAt || 0;
      const bMs = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || b.clientAt || 0;
      return aMs - bMs;
    });
}

/** Subscribe to messages in a thread (same chat for student and admin). */
export function subscribeThreadMessages(studentUserId, callback) {
  if (!studentUserId) return () => {};
  const q = query(
    collection(db, 'messages'),
    where('studentUserId', '==', studentUserId),
    limit(200)
  );
  return onSnapshot(
    q,
    (snap) => {
      callback(sortMessages(snap.docs));
    },
    () => callback([])
  );
}

/** Admin: all conversation threads */
export function subscribeAllThreads(callback) {
  const q = query(collection(db, 'messageThreads'), orderBy('lastMessageAt', 'desc'), limit(50));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

export async function markThreadRead(studentUserId, readerRole) {
  if (!studentUserId) return;
  const q = query(
    collection(db, 'messages'),
    where('studentUserId', '==', studentUserId),
    limit(200)
  );
  try {
    const snap = await getDocs(q);
    await Promise.all(
      snap.docs
        .filter((d) => d.data().fromRole !== readerRole && !d.data().read)
        .map((d) => updateDoc(doc(db, 'messages', d.id), { read: true }))
    );
  } catch {
    // non-fatal
  }
}

export { threadIdFor };
